import Form, {
	FormField,
	FormObject,
	FormOptions,
} from "../components/form/form.component";
import Title, { TitleProps } from "../components/title/title.component";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { handleReCaptchaVerifyUtil } from "@functions/reacptchaCallback";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useCallback, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
	isUserNameExists,
	setAffiliateData,
	validateEmail,
	validateIsEmailExists,
} from "../functions";
import RegisterService from "@services/register-service";
import { useLocation } from "react-router";
// import ReactGA from "react-ga";
import { setCustomer } from "@store/account/account.slice";
import { useDispatch } from "react-redux";
import { isCn } from "@utils/general";

// const TRACKING_ID = "UA-84115608-4"; // OUR_TRACKING_ID
// ReactGA.initialize(TRACKING_ID);

export interface RegisterNewStep1Props {
	goToNextStep: () => void;
}
export interface RegisterNewSetp1PersonalInformationValues {
	firstName: string;
	lastName: string;
	phone: string;
	country: string;
	social: string;
	socialType: "skype" | "telegram" | "";
}
export interface RegisterNewSetp1LoginInformationValues {
	email: string;
	proxyUsername: string;
	password: string;
	passwordConfirm: string;
}
export interface MarketingData {
	utm_campaign: string;
	ref: string;
	utm_medium: string;
	utm_source: string;
	utm_term: string;
	utm_content: string;
	utm_adgroup: string;
}

class RegisterNewSetp1PersonalInformationObject extends FormObject<RegisterNewSetp1PersonalInformationValues> {
	firstName: FormField;
	lastName: FormField;
	phone: FormField;
	country: FormField;
	socialType: FormField;
	social: FormField;

	constructor(
		options: FormOptions<RegisterNewSetp1PersonalInformationValues> | null,
	) {
		if (options) super(options);
		else super(null);
		this.firstName = {
			name: "firstName",
			displayName: "First Name",
			type: "text",
		};
		this.lastName = {
			name: "lastName",
			displayName: "Last Name",
			type: "text",
		};
		this.phone = { name: "phone", displayName: "Phone", type: "phone" };
		this.country = { name: "country", displayName: "Country", type: "country" };
		this.socialType = {
			name: "socialType",
			displayName: "Social Type",
			type: "select",
			select: {
				title: "Social Platform",
				values: ["telegram", "skype", "linkedin", "discord", "qq", "wechat"],
			},
		};
		this.social = { name: "social", displayName: "Social", type: "text" };
	}
	static getInitialValuesStatic(): RegisterNewSetp1PersonalInformationValues {
		return new RegisterNewSetp1PersonalInformationObject(
			null,
		).getInitialValues();
	}
}
class RegisterNewSetp1LoginInformationObject extends FormObject<RegisterNewSetp1LoginInformationValues> {
	email: FormField;
	proxyUsername: FormField;
	password: FormField;
	passwordConfirm: FormField;

	constructor(
		options: FormOptions<RegisterNewSetp1LoginInformationValues> | null,
	) {
		if (options) super(options);
		else super(null);
		this.email = { name: "email", displayName: "Work Email", type: "text" };
		this.proxyUsername = {
			name: "proxyUsername",
			displayName: "Proxy Username",
			type: "text",
		};
		this.password = {
			name: "password",
			displayName: "Password",
			type: "password",
		};
		this.passwordConfirm = {
			name: "passwordConfirm",
			displayName: "Confirm password",
			type: "password",
		};
	}
	static getInitialValuesStatic(): RegisterNewSetp1LoginInformationValues {
		return new RegisterNewSetp1LoginInformationObject(null).getInitialValues();
	}
}
const RegisterNewStep1 = (props: RegisterNewStep1Props) => {
	const { search } = useLocation();
	const { t } = useTranslation();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const handleReCaptchaVerify = handleReCaptchaVerifyUtil(
		useCallback,
		executeRecaptcha,
	);
	const theme = useTheme();
	const dispatch = useDispatch();

	const [fieldValueLoaded, setFieldValueLoaded] = useState<
		keyof RegisterNewSetp1LoginInformationValues | null
	>(null);

	const titlePropsPersonalInformation: TitleProps = {
		title: "Personal information",
		subTitle: "This information will be used for all the platform actions",
	};
	const titlePropsLoginInformation: TitleProps = {
		title: "Login information",
		subTitle: "This information will be used for all the platform actions",
	};
	const validationSchemaPersonalInformation: yup.ObjectSchema<
		yup.AnyObject,
		yup.AnyObject,
		yup.AnyObject,
		""
	> = yup.object({
		firstName: yup
			.string()
			.trim()
			.matches(/^([^0-9]*)$/g, "This field cannot contain numbers")
			.matches(/^[a-zA-Z]+$/, "Please enter only English characters.")
			.required(t("First name is required")!)
			.min(1, "Must be at least 1 character")
			.max(20, "Must be at most 20 character"),
		lastName: yup
			.string()
			.trim()
			.matches(/^([^0-9]*)$/g, "This field cannot contain numbers")
			.matches(/^[a-zA-Z]+$/, "Please enter only English characters.")
			.required(t("Last name is required")!)
			.min(1, "Must be at least 1 character")
			.max(20, "Must be at most 20 character"),
		phone: yup.string().trim().required(t("Phone is required")!),
		country: yup.string().required(t("Country is required")!),
		socialType: yup.string().required(t("Social platform type is required")!),
		social: yup
			.string()
			.trim()
			.required(t("Social platform contact is required")!)
			.min(1, "Must be at least 1 character")
			.max(50, "Must be at most 50 character"),
	});

	const validationSchemaLoginInformation: yup.ObjectSchema<
		yup.AnyObject,
		yup.AnyObject,
		yup.AnyObject,
		""
	> = yup.object({
		email: yup
			.string()
			.trim()
			.email(t("Enter a valid email")!)
			.required(t("Email is required")!)
			.test({
				name: "isExists",
				test: async (email, { createError }) => {
					setFieldValueLoaded(null);

					if (!email) {
						return true;
					}

					if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
						return createError({
							message: t(t("Email not valid.")) as string,
						});
					}

					setFieldValueLoaded("email");

					const isExists = await validateIsEmailExists(
						email,
						handleReCaptchaVerify,
					);

					if (isExists) {
						setFieldValueLoaded(null);
						return createError({
							message: t("Email already registered.") as string,
						});
					}

					const isDisposable = await validateEmail(
						email,
						handleReCaptchaVerify,
					);

					if (isDisposable) {
						setFieldValueLoaded(null);
						return createError({
							message: t("Email not valid.") as string,
						});
					}

					setFieldValueLoaded(null);
					return true;
				},
			}),
		proxyUsername: yup
			.string()
			.trim()
			.required(t("Username is required")!)
			.test({
				name: "isExists",
				test: async (username, { createError }) => {
					setFieldValueLoaded(null);
					if (!username) {
						return true;
					}
					if (!/^[a-zA-Z0-9_]+$/.test(username)) {
						return createError({
							message: t(
								"Login name must only contain letters, numbers, or underscores",
							) as string,
						});
					}

					setFieldValueLoaded("proxyUsername");
					const isExists = await isUserNameExists(
						username,
						handleReCaptchaVerify,
					);

					if (isExists) {
						setFieldValueLoaded(null);
						return createError({
							message: t("User already exists") as string,
						});
					}
					setFieldValueLoaded(null);
					return true;
				},
			})
			.min(1, "Must be at least 1 character")
			.max(50, "Must be at most 50 character"),
		password: yup.string().required(t("Password is required")!),
		passwordConfirm: yup
			.string()
			.required()
			.oneOf([yup.ref("password"), ""], t("Passwords must match")!),
	});

	const [formValuesPersonalInformation, setFormValuesPersonalInformation] =
		useState<RegisterNewSetp1PersonalInformationValues>(
			RegisterNewSetp1PersonalInformationObject.getInitialValuesStatic(),
		);
	const [formValuesLoginInformation, setFormValuesLoginInformation] =
		useState<RegisterNewSetp1LoginInformationValues>(
			RegisterNewSetp1LoginInformationObject.getInitialValuesStatic(),
		);

	const [isFormValidPersonalInformation, setIsFormValidPersonalInformation] =
		useState<boolean>(false);
	const [isFormValidLoginInformation, setIsFormValidLoginInformation] =
		useState<boolean>(false);

	const onSubmitForm = async () => {
		const registerService = new RegisterService();
		await registerService.registerForm1(
			formValuesPersonalInformation,
			formValuesLoginInformation,
			setAffiliateData(search),
			await handleReCaptchaVerify(),
		);
	};

	const onSubmitOtp = async (
		otp: string,
		setError: React.Dispatch<React.SetStateAction<boolean>>,
	): Promise<void> => {
		// ReactGA.event({
		// 	category: "User",
		// 	action: "first step",
		// 	label: "Complete the first step",
		// 	value: 1,
		// });

		const registerService = new RegisterService();
		const verifyRegisterResponse = await registerService.verifyRegsiter(
			formValuesPersonalInformation,
			formValuesLoginInformation,
			setAffiliateData(search),
			otp,
			await handleReCaptchaVerify(),
		);

		if (!verifyRegisterResponse.success) return setError(true);
		dispatch(setCustomer(verifyRegisterResponse.customer));

		try {
			sessionStorage.setItem("tempToken", verifyRegisterResponse.token);
			sessionStorage.setItem(
				"tempCust",
				JSON.stringify(verifyRegisterResponse.customer),
			);
			if (!isCn()) {
				(window as any).ym(88190843, "reachGoal", "email_verif");
			}
		} catch (e) {
			console.error(e);
		}
		props.goToNextStep();
	};
	const registerNewStepPersonalInformation1Object =
		new RegisterNewSetp1PersonalInformationObject({
			formName: "register-step-1-personal-information",
			otp: {
				isOtp: false,
				email: "",
			},
			submit: {
				isSubmitActive: false,
			},
			setIsFormValid: setIsFormValidPersonalInformation,
			validationSchema: validationSchemaPersonalInformation,
			setFormValues: setFormValuesPersonalInformation,
		});
	const registerNewStep1LoginInformationObject =
		new RegisterNewSetp1LoginInformationObject({
			formName: "register-step-1-login-information",
			otp: {
				isOtp: true,
				onSubmit: onSubmitOtp,
				email: formValuesLoginInformation.email,
			},
			submit: {
				isSubmitActive: true,
				isSubmitApproved:
					isFormValidPersonalInformation && isFormValidLoginInformation,
				onSubmit: onSubmitForm,
			},
			goToNextStep: props.goToNextStep,
			setIsFormValid: setIsFormValidLoginInformation,
			validationSchema: validationSchemaLoginInformation,
			setFormValues: setFormValuesLoginInformation,
		});
	return (
		<Box
			style={{
				backgroundColor: theme.palette.background.paper,
				padding: "14px",
			}}
		>
			<Title {...titlePropsPersonalInformation}></Title>
			<Form {...registerNewStepPersonalInformation1Object.getProps()}></Form>
			<Title {...titlePropsLoginInformation}></Title>
			<Form
				{...registerNewStep1LoginInformationObject.getProps()}
				fieldValueLoaded={fieldValueLoaded}
			></Form>
			{/* Privacy Policy statement and link */}
			<Box
				style={{
					marginTop: "20px",
					marginBottom: "20px",
				}}
			>
				<Typography variant="body2">
					Your details will be processed in accordance with our
					<a
						href="https://netnut.io/privacy-policy/"
						target="_blank"
						rel="noopener noreferrer"
					>
						{" "}
						privacy policy
					</a>
					.
				</Typography>
			</Box>
		</Box>
	);
};
export default RegisterNewStep1;
